import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@Totum/Main/Constants";
import { IQuestion, IQuestionnaireDetails } from "@Totum/QuestionnaireBuilder/Domain/types";
import {
    createQuestion,
    createQuestionnaire,
    deleteQuestion,
    deleteQuestionnaire,
    publishQuestionnaire,
    updateQuestion,
    updateQuestionnaire,
} from "@Totum/QuestionnaireBuilder/Services/apiService";
import useCurrentQuestionnaireId from "@Totum/QuestionnaireBuilder/State/hooks/useCurrentQuestionnaireId";

/**
 * Mutation for creating a new questionnaire.
 *
 * @example
 * const { mutate: createQuestionnaire } = useCreateQuestionnaireMutation();
 *
 * const handleCreate = (data: IQuestionnaireDetails) => {
 *   createQuestionnaire(data);
 * };
 */
export const useCreateQuestionnaireMutation = () => {
    const queryClient = useQueryClient();
    const [_, setCurrentQuestionnaireId] = useCurrentQuestionnaireId();
    return useMutation(async (model: IQuestionnaireDetails) => createQuestionnaire(model), {
        onSuccess: async (result) => {
            if (result.isSuccess) {
                setCurrentQuestionnaireId(result.value.questionnaireId);
                await queryClient.invalidateQueries([QueryKeys.QuestionnaireList]);
            }
        },
    });
};

/**
 * Mutation for updating an existing questionnaire with optimistic updates.
 *
 * @example
 * const { mutate: updateQuestionnaire } = useUpdateQuestionnaireMutation();
 *
 * const handleUpdate = (data: IQuestionnaireDetails) => {
 *   updateQuestionnaire(data);
 * };
 */
export const useUpdateQuestionnaireMutation = () => {
    const queryClient = useQueryClient();

    return useMutation(async (model: IQuestionnaireDetails) => updateQuestionnaire(model), {
        onMutate: async (updatedModel) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries([QueryKeys.QuestionnaireCurrent]);

            // Snapshot the previous value
            const previousQuestionnaire = queryClient.getQueryData([QueryKeys.QuestionnaireCurrent]);

            // Optimistically update to the new value
            queryClient.setQueryData([QueryKeys.QuestionnaireCurrent], updatedModel);

            // Return a context object with the snapshotted value
            return { previousQuestionnaire };
        },
        onError: (_err, _updatedModel, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            queryClient.setQueryData([QueryKeys.QuestionnaireCurrent], context?.previousQuestionnaire);
        },
        onSettled: async (result) => {
            if (result?.isSuccess) {
                // Invalidate and refetch after the mutation is settled
                await queryClient.invalidateQueries([QueryKeys.QuestionnaireCurrent]);
                await queryClient.invalidateQueries([QueryKeys.QuestionnaireList]);
            }
        },
    });
};

/**
 * Mutation for publishing a questionnaire.
 *
 * @example
 * const { mutate: publishQuestionnaire } = usePublishQuestionnaireMutation();
 *
 * const handlePublish = (questionnaireId: string) => {
 *   publishQuestionnaire(questionnaireId);
 * };
 */
export const usePublishQuestionnaireMutation = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async (questionnaireId: IQuestionnaireDetails["questionnaireId"]) => publishQuestionnaire(questionnaireId),
        {
            onSuccess: async (result) => {
                if (result.isSuccess) {
                    await queryClient.invalidateQueries([QueryKeys.QuestionnaireList]);
                }
            },
        }
    );
};

/**
 * Mutation for deleting a questionnaire.
 *
 * @example
 * const { mutate: deleteQuestionnaire } = useDeleteQuestionnaireMutation();
 *
 * const handleDelete = (questionnaireId: string) => {
 *   deleteQuestionnaire(questionnaireId);
 * };
 */
export const useDeleteQuestionnaireMutation = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async (questionnaireId: IQuestionnaireDetails["questionnaireId"]) => deleteQuestionnaire(questionnaireId),
        {
            onSuccess: async (result) => {
                if (result.isSuccess) {
                    await queryClient.invalidateQueries([QueryKeys.QuestionnaireList]);
                }
            },
        }
    );
};

/**
 * Mutation for creating a new question within a questionnaire.
 *
 * @example
 * const { mutate: createQuestion } = useCreateQuestionMutation();
 *
 * const handleCreateQuestion = (questionnaireId: string, questionData: IQuestion) => {
 *   createQuestion({ questionnaireId, model: questionData });
 * };
 */
export const useCreateQuestionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation(async (model: IQuestion) => createQuestion(model), {
        onSuccess: async (result) => {
            if (result.isSuccess) {
                await queryClient.invalidateQueries([QueryKeys.QuestionnaireCurrent]);
            }
        },
    });
};

/**
 * Mutation for updating a question within a questionnaire.
 */
export const useUpdateQuestionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async (model: IQuestion) => {
            return updateQuestion(model);
        },
        {
            async onMutate(updatedModel) {
                // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
                await queryClient.cancelQueries([QueryKeys.QuestionnaireCurrent]);

                // Snapshot the previous value of the entire questionnaire
                const previousQuestionnaire = queryClient.getQueryData<IQuestionnaireDetails>([
                    QueryKeys.QuestionnaireCurrent,
                ]);

                // Update the question within the questionnaire
                const updatedQuestionnaire = previousQuestionnaire?.questions?.map((question) =>
                    question.questionId === updatedModel.questionId ? updatedModel : question
                );

                // Optimistically update to the new value
                queryClient.setQueryData([QueryKeys.QuestionnaireCurrent], () => updatedQuestionnaire);

                // Return a context object with the snapshotted value
                return { previousQuestionnaire };
            },
            onError: (_err, _updatedModel, context) => {
                // If the mutation fails, use the context returned from onMutate to roll back
                queryClient.setQueryData([QueryKeys.QuestionnaireCurrent], context?.previousQuestionnaire);
            },
            onSuccess: async (result) => {
                if (result.isSuccess) {
                    await queryClient.invalidateQueries([QueryKeys.QuestionnaireList]);
                }
            },
        }
    );
};

/**
 * Mutation for deleting a question from a questionnaire.
 *
 * @example
 * const { mutate: deleteQuestion } = useDeleteQuestionMutation();
 *
 * const handleDeleteQuestion = (questionnaireId: string, questionId: string) => {
 *   deleteQuestion({ questionnaireId, questionId });
 * };
 */
export const useDeleteQuestionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation(async (questionId: IQuestion["questionId"]) => deleteQuestion(questionId), {
        onSuccess: async (result) => {
            if (result.isSuccess) {
                await queryClient.invalidateQueries([QueryKeys.QuestionnaireCurrent]);
            }
        },
    });
};
